import { AlertTriangle, CheckCircle2, Info, RefreshCcw, XCircle } from "lucide-react";
import { useMemo } from "react";

import { useAuth } from "@/auth/useAuth";
import { getRuntimeConfig } from "@/config/runtimeConfig";
import {
  buildSamlEmailDiagnostic,
  clearStoredAuthError,
  readStoredAuthError,
  type ClaimRow,
  type SamlEmailDiagnostic as DiagnosticModel,
} from "@/utils/samlDiagnostic";

/**
 * "Azure AD / Entra External ID SAML Email Diagnostic" — a diagnostic
 * panel rendered on the Tokens page to help triage AADSTS5000819
 * ("SAML Assertion is invalid. Email address claim is missing or does
 * not match domain from an external realm.") errors.
 *
 * Everything on this panel is read-only — it does not mutate tokens,
 * the MSAL cache, or any remote service. The only stateful behaviour
 * is a button that clears the captured auth error from sessionStorage
 * so a subsequent successful sign-in will not keep showing the stale
 * failure.
 */
export function SamlEmailDiagnostic() {
  const { account, claims } = useAuth();
  const cfg = getRuntimeConfig();

  const diagnostic: DiagnosticModel = useMemo(() => {
    const stored = readStoredAuthError();
    return buildSamlEmailDiagnostic({
      claims: (claims as Record<string, unknown> | null) ?? null,
      accountUsername: account?.username ?? null,
      expectedEmailDomain: cfg.expectedEmailDomain,
      lastError: stored ?? null,
    });
  }, [account, claims, cfg.expectedEmailDomain]);

  return (
    <section className="card p-4" data-testid="saml-email-diagnostic">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Azure AD / Entra External ID SAML Email Diagnostic
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-slate-600">
            Helps diagnose{" "}
            <code className="kbd">AADSTS5000819</code> – "SAML Assertion is
            invalid. Email address claim is missing or does not match domain
            from an external realm." Entra External ID raises this when the
            federated identity provider's SAML assertion does not carry an
            acceptable email claim, or when the email's domain differs from
            the external realm configured in Entra.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            clearStoredAuthError();
            window.location.reload();
          }}
          className="btn-ghost text-xs"
          title="Clear the captured auth error from sessionStorage"
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          Clear captured error
        </button>
      </header>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ErrorCard d={diagnostic} />
        <EmailCard d={diagnostic} />
      </div>

      <SummaryCard d={diagnostic} />

      <ClaimsTable rows={diagnostic.claims} />

      <CauseList />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Subsections                                                        */
/* ------------------------------------------------------------------ */

function ErrorCard({ d }: { d: DiagnosticModel }) {
  const { error } = d;
  return (
    <div className="rounded border border-slate-200 bg-slate-50/60 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Captured auth error
      </h3>
      <dl className="mt-2 grid grid-cols-1 gap-y-1.5 text-xs sm:grid-cols-[140px_1fr]">
        <DtDdRow label="Error Code" value={error.errorCode} variant={error.errorCode ? "error" : "empty"} />
        <DtDdRow label="Error Message" value={error.errorMessage} variant={error.errorMessage ? "error" : "empty"} mono />
        <DtDdRow label="Trace ID" value={error.traceId} variant={error.traceId ? "warning" : "empty"} mono />
        <DtDdRow label="Correlation ID" value={error.correlationId} variant={error.correlationId ? "warning" : "empty"} mono />
        <DtDdRow label="Timestamp" value={error.timestamp} variant={error.timestamp ? "ok" : "empty"} />
        <DtDdRow label="Sub-error" value={error.subError} variant={error.subError ? "warning" : "empty"} />
      </dl>
    </div>
  );
}

function EmailCard({ d }: { d: DiagnosticModel }) {
  const expectedValue = d.expectedDomain ?? "[expected domain not found in app config]";
  const expectedVariant: Variant = d.expectedDomain ? "ok" : "warning";

  let matchVariant: Variant;
  let matchLabel: string;
  switch (d.domainCheck.kind) {
    case "match":
      matchVariant = "ok";
      matchLabel = `match (${d.domainCheck.actual} == ${d.domainCheck.expected})`;
      break;
    case "mismatch":
      matchVariant = "error";
      matchLabel = `mismatch (${d.domainCheck.actual || "(empty)"} ≠ ${d.domainCheck.expected})`;
      break;
    case "no-email":
      matchVariant = "error";
      matchLabel = "cannot check — no email detected";
      break;
    case "no-expected":
      matchVariant = "warning";
      matchLabel = "cannot check — expected domain not configured";
      break;
  }

  // "Missing required field" = any of the preferred email-bearing
  // claims is missing OR no detected email at all.
  const missingField = d.detectedEmail ? null : "emailaddress / email / mail / upn";

  return (
    <div className="rounded border border-slate-200 bg-slate-50/60 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Email / domain resolution
      </h3>
      <dl className="mt-2 grid grid-cols-1 gap-y-1.5 text-xs sm:grid-cols-[180px_1fr]">
        <DtDdRow
          label="Final detected email value"
          value={d.detectedEmail}
          variant={d.detectedEmail ? "ok" : "error"}
          mono
          hint={d.detectedEmailSource ? `source: ${d.detectedEmailSource}` : undefined}
        />
        <DtDdRow
          label="Detected email domain"
          value={d.detectedDomain}
          variant={d.detectedDomain ? "ok" : "error"}
          mono
        />
        <DtDdRow
          label="Expected external realm/domain"
          value={expectedValue}
          variant={expectedVariant}
          mono
        />
        <DtDdRow
          label="Domain match result"
          value={matchLabel}
          variant={matchVariant}
        />
        <DtDdRow
          label="Missing required field result"
          value={missingField ? `missing: ${missingField}` : "all preferred email claims present"}
          variant={missingField ? "error" : "ok"}
        />
        <DtDdRow
          label="NameID value"
          value={d.nameIdDisplay}
          variant={d.nameIdDisplay !== "empty" ? "ok" : "empty"}
          mono
        />
        <DtDdRow
          label="NameID looks like email"
          value={d.nameIdIsEmail ? "yes" : "no"}
          variant={d.nameIdIsEmail ? "ok" : "warning"}
        />
      </dl>
    </div>
  );
}

function SummaryCard({ d }: { d: DiagnosticModel }) {
  let icon;
  let variantClass: string;

  if (!d.detectedEmail || d.domainCheck.kind === "mismatch") {
    icon = <XCircle className="h-4 w-4 text-red-600" />;
    variantClass = "border-red-200 bg-red-50";
  } else if (d.domainCheck.kind === "no-expected") {
    icon = <AlertTriangle className="h-4 w-4 text-amber-600" />;
    variantClass = "border-amber-200 bg-amber-50";
  } else {
    icon = <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    variantClass = "border-emerald-200 bg-emerald-50";
  }

  return (
    <div className={`mt-4 rounded border p-3 ${variantClass}`}>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
        {icon} Summary
      </h3>
      <p className="mt-1.5 text-sm text-slate-800">{d.summary}</p>
      <h4 className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
        Suggested fix
      </h4>
      <p className="mt-1 text-xs text-slate-700">{d.suggestedFix}</p>
    </div>
  );
}

function ClaimsTable({ rows }: { rows: ClaimRow[] }) {
  return (
    <div className="mt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Claims Checked
      </h3>
      <div className="mt-2 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-xs">
          <thead className="bg-slate-50 text-left font-semibold text-slate-600">
            <tr>
              <th className="px-3 py-2">Claim Name</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2 w-28">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((row) => (
              <tr key={row.name}>
                <td className="px-3 py-2 align-top">
                  <div className="break-all font-medium text-slate-800">{row.name}</div>
                  {row.resolvedKey && row.resolvedKey !== row.name ? (
                    <div className="mt-0.5 text-[10.5px] text-slate-400">
                      matched key: <code>{row.resolvedKey}</code>
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2 align-top font-mono text-[11.5px] text-slate-700">
                  {renderValue(row.value, row.status)}
                </td>
                <td className="px-3 py-2 align-top">
                  <StatusChip status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CauseList() {
  return (
    <div className="mt-4 rounded border border-slate-200 bg-white p-3">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Info className="h-3.5 w-3.5 text-slate-400" />
        Common causes of AADSTS5000819
      </h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-700">
        <li>The SAML assertion does not contain an email address claim.</li>
        <li>The email address claim exists but is empty.</li>
        <li>
          The email address claim exists but the domain does not match the
          external realm/domain configured in Microsoft Entra External ID.
        </li>
        <li>
          The SAML NameID is persistent or opaque instead of an email address.
        </li>
        <li>
          Entra External ID is using NameID or a mapped claim as the primary
          identifier and cannot validate the domain.
        </li>
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Primitives                                                         */
/* ------------------------------------------------------------------ */

type Variant = "ok" | "warning" | "error" | "empty";

function DtDdRow({
  label,
  value,
  variant,
  mono,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  variant: Variant;
  mono?: boolean;
  hint?: string;
}) {
  return (
    <>
      <dt className="font-semibold text-slate-500">{label}</dt>
      <dd className={`min-w-0 ${mono ? "font-mono text-[11.5px]" : ""} text-slate-800`}>
        <span className={variantClass(variant)}>{renderText(value, variant)}</span>
        {hint ? (
          <span className="ml-2 text-[10.5px] font-normal text-slate-400">({hint})</span>
        ) : null}
      </dd>
    </>
  );
}

function StatusChip({ status }: { status: ClaimRow["status"] }) {
  switch (status) {
    case "present":
      return <span className="diagnostic-ok">present</span>;
    case "empty":
      return <span className="diagnostic-warning">empty</span>;
    case "missing":
    default:
      return <span className="diagnostic-error">missing</span>;
  }
}

function renderValue(
  value: string | null,
  status: ClaimRow["status"],
): JSX.Element {
  if (status === "missing") return <span className="diagnostic-error">[missing]</span>;
  if (status === "empty" || !value) return <span className="diagnostic-empty">[empty]</span>;
  return <span className="diagnostic-ok break-all">{value}</span>;
}

function renderText(value: string | null | undefined, variant: Variant): string {
  if (value === null || value === undefined || value === "") {
    return variant === "warning" ? "[missing]" : "[missing]";
  }
  return value;
}

function variantClass(variant: Variant): string {
  switch (variant) {
    case "ok":
      return "diagnostic-ok";
    case "warning":
      return "diagnostic-warning";
    case "error":
      return "diagnostic-error";
    case "empty":
    default:
      return "diagnostic-empty";
  }
}
